import { Component, Event, h, Prop, State } from '@stencil/core';
import { DateUtils, PersonDoc } from 'common';
import { DateSelector } from '../../global/ui/date-selector';
import { SubmittalPopulated } from '..';

@Component({
  tag: 'concourse-submittals-filter',
  styleUrl: 'concourse-submittals-filter.css',
})
export class ConcourseSubmittalsFilter {
  @Prop() column: string;
  @Prop() columnName: string;
  @Prop() submittals: SubmittalPopulated[] = [];
  @Prop() people: PersonDoc[] = [];
  @Prop() currentFilterValues: any = {};
  @State() selectedValues: string[] = [];
  @State() fromDate: string = '';
  @State() toDate: string = '';
  @State() rangeValue: { lower: number; upper: number } = { lower: 0, upper: 100 };
  @State() rangeMin: number = 0;
  @State() rangeMax: number = 100;
  @Event({ bubbles: true }) filterApplied;

  componentWillLoad() {
    // Initialize selected values based on column type
    if (this.isDateColumn(this.column)) {
      // For date columns, set default date range based on data
      this.selectedValues = [];
      if (this.currentFilterValues.fromDate && this.currentFilterValues.toDate) {
        // Use existing filter values
        this.fromDate = this.currentFilterValues.fromDate;
        this.toDate = this.currentFilterValues.toDate;
      } else {
        // Set default date range
        this.setDefaultDateRange();
      }
    } else if (this.isRangeColumn(this.column)) {
      // For range columns, set default range based on data
      this.selectedValues = [];
      this.setRangeBounds();
      if (this.currentFilterValues.rangeValue) {
        this.rangeValue = { ...this.currentFilterValues.rangeValue };
      } else {
        this.rangeValue = { lower: this.rangeMin, upper: this.rangeMax };
      }
    } else {
      if (this.currentFilterValues.selectedValues) {
        // Use existing filter values
        this.selectedValues = this.currentFilterValues.selectedValues;
      } else {
        // Set default values
        this.selectedValues = this.getUniqueValues();
      }
    }
  }

  getUniqueValues(): string[] {
    if (!this.submittals || this.submittals.length === 0) return [];

    const values = new Set<string>();

    switch (this.column) {
      case 'status':
        this.submittals.forEach(submittal => {
          if (submittal.statusName) values.add(submittal.statusName);
        });
        break;
      case 'assignee':
        this.submittals.forEach(submittal => {
          submittal.assigneeNames.forEach(name => {
            if (name.trim()) values.add(name.trim());
          });
        });
        break;
      case 'division':
        this.submittals.forEach(submittal => {
          if (submittal.divisionTitle) values.add(submittal.divisionTitle);
        });
        break;
      case 'specSection':
        this.submittals.forEach(submittal => {
          if (submittal.specSectionNum) values.add(submittal.specSectionNum);
        });
        break;
      case 'submittalType':
        this.submittals.forEach(submittal => {
          if (submittal.submittalType) values.add(submittal.submittalType);
        });
        break;
      case 'packageTitle':
        this.submittals.forEach(submittal => {
          if (submittal.packageTitle) values.add(submittal.packageTitle);
        });
        break;
      default:
        return [];
    }

    return Array.from(values).sort();
  }

  isDateColumn(column: string): boolean {
    return ['dueDate', 'workflowDueDate', 'statusDueDate'].includes(column);
  }

  isRangeColumn(column: string): boolean {
    return ['lateDays'].includes(column);
  }

  setDefaultDateRange() {
    if (!this.submittals || this.submittals.length === 0) return;

    const dates = this.submittals.map(submittal => this.getDateForColumn(submittal, this.column)).filter(date => date && !isNaN(date.getTime())); // Filter out null/undefined/invalid dates

    if (dates.length > 0) {
      const minDate = new Date(Math.min(...dates.map(d => d.getTime())));
      const maxDate = new Date(Math.max(...dates.map(d => d.getTime())));

      this.fromDate = this.toDateKey(minDate);
      this.toDate = this.toDateKey(maxDate);
    }
  }

  setRangeBounds() {
    if (!this.submittals || this.submittals.length === 0) return;

    const values = this.submittals.map(submittal => this.getRangeValueForColumn(submittal, this.column)).filter(value => value !== null && value !== undefined);

    if (values.length > 0) {
      this.rangeMin = Math.min(...values);
      this.rangeMax = Math.max(...values);
    }
  }

  setDefaultRange() {
    this.setRangeBounds();
    this.rangeValue = { lower: this.rangeMin, upper: this.rangeMax };
  }

  getRangeValueForColumn(submittal: SubmittalPopulated, column: string): number | null {
    switch (column) {
      case 'lateDays':
        return submittal.lateDays;
      default:
        return null;
    }
  }

  getDateForColumn(submittal: SubmittalPopulated, column: string): Date | null {
    switch (column) {
      case 'dueDate':
        return submittal.dueDate;
      case 'workflowDueDate':
        return submittal.workflowDueDate;
      case 'statusDueDate':
        return submittal.statusDueDate;
      default:
        return null;
    }
  }

  toggleValue(value: string) {
    if (this.selectedValues.includes(value)) {
      this.selectedValues = this.selectedValues.filter(v => v !== value);
    } else {
      this.selectedValues = [...this.selectedValues, value];
    }
  }

  selectAll() {
    this.selectedValues = [...this.getUniqueValues()];
  }

  selectNone() {
    this.selectedValues = [];
  }

  applyFilter() {
    if (this.isDateColumn(this.column)) {
      this.filterApplied.emit({
        column: this.column,
        selectedValues: [],
        fromDate: this.fromDate,
        toDate: this.toDate,
      });
    } else if (this.isRangeColumn(this.column)) {
      this.filterApplied.emit({
        column: this.column,
        selectedValues: [],
        rangeValue: this.rangeValue,
      });
    } else {
      this.filterApplied.emit({
        column: this.column,
        selectedValues: this.selectedValues,
      });
    }
  }

  clearFilter() {
    this.selectedValues = [];
    if (this.isDateColumn(this.column)) {
      // Clear date filters by setting empty values
      this.fromDate = '';
      this.toDate = '';
      this.filterApplied.emit({
        column: this.column,
        selectedValues: [],
        fromDate: '',
        toDate: '',
      });
    } else if (this.isRangeColumn(this.column)) {
      // Clear range filter by emitting null
      this.setDefaultRange(); // Reset UI to full range
      this.filterApplied.emit({
        column: this.column,
        selectedValues: [],
        rangeValue: null,
      });
    } else {
      this.filterApplied.emit({
        column: this.column,
        selectedValues: [],
      });
    }
  }

  async selectFromDate() {
    const selectedDate = await DateSelector.present('Select From Date', this.fromDate);
    if (selectedDate) {
      this.fromDate = selectedDate.slice(0, 10);
    }
  }

  async selectToDate() {
    const selectedDate = await DateSelector.present('Select To Date', this.toDate);
    if (selectedDate) {
      this.toDate = selectedDate.slice(0, 10);
    }
  }

  toDateKey(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  formatDateForDisplay(dateString: string): string {
    if (!dateString) return '';
    const [year, month, day] = dateString.slice(0, 10).split('-').map(Number);
    return DateUtils.format(new Date(year, month - 1, day));
  }

  onRangeChange(event: CustomEvent) {
    this.rangeValue = { ...event.detail.value };
  }

  render() {
    const uniqueValues = this.getUniqueValues();
    const allSelected = uniqueValues.length > 0 && this.selectedValues.length === uniqueValues.length;
    const someSelected = this.selectedValues.length > 0 && this.selectedValues.length < uniqueValues.length;
    const isDateColumn = this.isDateColumn(this.column);
    const isRangeColumn = this.isRangeColumn(this.column);
    const hasDateFilter = isDateColumn && (this.fromDate !== '' || this.toDate !== '');
    const hasRangeFilter = isRangeColumn && (this.rangeValue.lower > this.rangeMin || this.rangeValue.upper < this.rangeMax);
    const hasValueFilter = !isDateColumn && !isRangeColumn && this.selectedValues.length > 0;

    return (
      <div class="filter-popover">
        <div class="filter-header">
          <h3>Filter by {this.columnName || this.column}</h3>
        </div>

        <div class="filter-content">
          {isDateColumn ? (
            <div class="date-filter-content">
              <div class="date-button-group">
                <ion-button onClick={() => this.selectFromDate()} class={this.fromDate ? 'date-selected' : ''}>
                  {this.fromDate ? this.formatDateForDisplay(this.fromDate) : 'Select From Date'}
                </ion-button>
                <ion-button onClick={() => this.selectToDate()} class={this.toDate ? 'date-selected' : ''}>
                  {this.toDate ? this.formatDateForDisplay(this.toDate) : 'Select To Date'}
                </ion-button>
              </div>
            </div>
          ) : isRangeColumn ? (
            <div class="range-filter-content">
              <div class="range-label">
                <span>
                  Range: {this.rangeValue.lower} - {this.rangeValue.upper} days
                </span>
              </div>
              <ion-range
                dualKnobs={true}
                value={this.rangeValue}
                onIonInput={e => this.onRangeChange(e)}
                min={this.rangeMin}
                max={this.rangeMax}
                step={1}
                pin={true}
                snaps={true}
              />
            </div>
          ) : (
            <ion-list lines="none">
              {uniqueValues.map(value => (
                <ion-item key={value}>
                  <ion-checkbox slot="start" checked={this.selectedValues.includes(value)} onIonChange={() => this.toggleValue(value)} />
                  <ion-label>{value}</ion-label>
                </ion-item>
              ))}
            </ion-list>
          )}
        </div>

        <div class="filter-footer">
          <ion-button fill="clear" onClick={() => this.clearFilter()} disabled={!hasDateFilter && !hasValueFilter && !hasRangeFilter}>
            Clear
          </ion-button>
          <ion-button color="primary" fill="solid" onClick={() => this.applyFilter()}>
            Apply
          </ion-button>
        </div>
      </div>
    );
  }
}
